// 广告点击报表页
//
// spec: docs/superpowers/specs/2026-06-29-agent-ads-design.md §报表
// 日期范围:近7天 / 近30天 / 自定义
// 内容:三张汇总卡(三 slot 总点击) + 趋势条形图 + 明细表
//
// 图表使用 recharts(项目已有;vite manualChunk 已包含)。

import { useMemo, useState } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Spinner } from '@dogdisk/shared'
import { AgentLayout } from '../components/agent-layout'
import { AD_SLOT_LABEL, AD_SLOT_TYPES } from '../_lib/ad-slot'
import { useAdReport } from '../_lib/agent-ads-api'

// ─── 日期工具 ────────────────────────────────────────────────────────────────

function toDateStr(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function daysAgo(n: number): string {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return toDateStr(d)
}

const TODAY = toDateStr(new Date())

type RangePreset = '7d' | '30d' | 'custom'

// ─── 颜色 ────────────────────────────────────────────────────────────────────

const SLOT_COLORS: Record<string, string> = {
  forum_list_icon: '#F59E0B',
  forum_list_card: '#FB923C',
  article_banner: '#10B981',
}

const SLOT_SHORT: Record<string, string> = {
  forum_list_icon: '小图标',
  forum_list_card: '瀑布卡片',
  article_banner: '横幅',
}

// ─── 主组件 ─────────────────────────────────────────────────────────────────

export function AgentAdsReportPage() {
  const [preset, setPreset] = useState<RangePreset>('7d')
  const [customFrom, setCustomFrom] = useState(daysAgo(6))
  const [customTo, setCustomTo] = useState(TODAY)

  const from = preset === '7d' ? daysAgo(6) : preset === '30d' ? daysAgo(29) : customFrom
  const to = preset === 'custom' ? customTo : TODAY

  const { data: rows, isLoading } = useAdReport(from, to)

  // ── 汇总(三 slot 各自区间总点击) ─────────────────────────────────────────

  const totals = useMemo(() => {
    const acc: Record<string, number> = {}
    for (const r of rows ?? []) {
      acc[r.slot_type] = (acc[r.slot_type] ?? 0) + Number(r.clicks)
    }
    return acc
  }, [rows])

  // ── 趋势数据(日期 × slot 点击) ──────────────────────────────────────────

  const trendData = useMemo(() => {
    // 聚合: { day: { slot_type: clicks } }
    const byDay = new Map<string, Record<string, number>>()
    for (const r of rows ?? []) {
      const existing = byDay.get(r.day) ?? {}
      existing[r.slot_type] = (existing[r.slot_type] ?? 0) + Number(r.clicks)
      byDay.set(r.day, existing)
    }
    // 排序日期
    const sorted = Array.from(byDay.entries()).sort(([a], [b]) => a.localeCompare(b))
    return sorted.map(([day, slotMap]) => ({
      day: day.slice(5), // MM-DD
      forum_list_icon: slotMap['forum_list_icon'] ?? 0,
      forum_list_card: slotMap['forum_list_card'] ?? 0,
      article_banner: slotMap['article_banner'] ?? 0,
    }))
  }, [rows])

  return (
    <AgentLayout topbarTitle="代理 · 广告报表">
      <div
        className="mx-auto flex max-w-[960px] flex-col gap-6 px-6 py-8"
        data-testid="ads-report-page"
      >
        {/* 标题 */}
        <header>
          <h1 className="text-xl font-bold text-gray-900">广告点击报表</h1>
          <p className="mt-1 text-sm text-gray-500">
            统计各广告位在所选时间范围内的点击量
          </p>
        </header>

        {/* 日期范围选择器 */}
        <section className="flex flex-wrap items-center gap-3">
          {(
            [
              { value: '7d', label: '近 7 天' },
              { value: '30d', label: '近 30 天' },
              { value: 'custom', label: '自定义' },
            ] as { value: RangePreset; label: string }[]
          ).map(({ value, label }) => (
            <button
              key={value}
              type="button"
              data-testid={`preset-${value}`}
              onClick={() => setPreset(value)}
              className={[
                'rounded-md px-4 py-1.5 text-sm font-medium ring-1 transition-colors',
                preset === value
                  ? 'bg-amber-500 text-white ring-amber-500'
                  : 'bg-white text-gray-600 ring-gray-200 hover:ring-amber-400',
              ].join(' ')}
            >
              {label}
            </button>
          ))}

          {preset === 'custom' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customFrom}
                max={customTo}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                data-testid="custom-from"
              />
              <span className="text-gray-400">—</span>
              <input
                type="date"
                value={customTo}
                min={customFrom}
                max={TODAY}
                onChange={(e) => setCustomTo(e.target.value)}
                className="rounded-md border border-gray-300 px-2 py-1 text-sm"
                data-testid="custom-to"
              />
            </div>
          )}
        </section>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Spinner className="text-amber-500" />
          </div>
        ) : (
          <>
            {/* 汇总卡(三 slot) */}
            <section
              className="grid grid-cols-3 gap-4"
              data-testid="summary-cards"
            >
              {AD_SLOT_TYPES.map((slot) => (
                <div
                  key={slot}
                  className="flex flex-col gap-1 rounded-xl border border-gray-200 bg-white p-5"
                  data-testid={`summary-${slot}`}
                >
                  <span className="text-xs font-medium text-gray-500">
                    {SLOT_SHORT[slot] ?? slot}
                  </span>
                  <span
                    className="text-2xl font-bold"
                    style={{ color: SLOT_COLORS[slot] ?? '#6B7280' }}
                  >
                    {(totals[slot] ?? 0).toLocaleString()}
                  </span>
                  <span className="text-[11px] text-gray-400">次点击</span>
                </div>
              ))}
            </section>

            {/* 趋势条形图 */}
            <section
              className="rounded-xl border border-gray-200 bg-white p-5"
              data-testid="trend-chart"
            >
              <h2 className="mb-4 text-sm font-semibold text-gray-700">按天趋势</h2>
              {trendData.length === 0 ? (
                <p className="py-10 text-center text-sm text-gray-400">
                  所选时间范围内暂无点击数据
                </p>
              ) : (
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={trendData} margin={{ top: 4, right: 8, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
                      <XAxis
                        dataKey="day"
                        tick={{ fontSize: 11, fill: '#9CA3AF' }}
                        tickLine={false}
                        axisLine={{ stroke: '#E5E7EB' }}
                      />
                      <YAxis
                        tick={{ fontSize: 11, fill: '#9CA3AF' }}
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: 8,
                          border: '1px solid #E5E7EB',
                          fontSize: 12,
                        }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 12 }}
                        formatter={(value: string) => SLOT_SHORT[value] ?? value}
                      />
                      {AD_SLOT_TYPES.map((slot) => (
                        <Bar
                          key={slot}
                          dataKey={slot}
                          fill={SLOT_COLORS[slot] ?? '#9CA3AF'}
                          radius={[2, 2, 0, 0]}
                          maxBarSize={24}
                          name={slot}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </section>

            {/* 明细表 */}
            <section
              className="rounded-xl border border-gray-200 bg-white"
              data-testid="detail-table"
            >
              <header className="border-b border-gray-100 px-5 py-3">
                <h2 className="text-sm font-semibold text-gray-700">明细</h2>
              </header>
              {(rows ?? []).length === 0 ? (
                <p className="py-10 text-center text-sm text-gray-400">暂无数据</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs font-medium text-gray-500">
                        <th className="px-5 py-2">日期</th>
                        <th className="px-5 py-2">广告位</th>
                        <th className="px-5 py-2 text-right">点击次数</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {(rows ?? []).map((r, i) => (
                        <tr
                          key={i}
                          className="hover:bg-gray-50"
                          data-testid={`detail-row-${r.day}-${r.slot_type}`}
                        >
                          <td className="px-5 py-2.5 font-mono text-xs text-gray-600">
                            {r.day}
                          </td>
                          <td className="px-5 py-2.5 text-xs text-gray-600">
                            {AD_SLOT_LABEL[r.slot_type as keyof typeof AD_SLOT_LABEL] ??
                              r.slot_type}
                          </td>
                          <td className="px-5 py-2.5 text-right font-medium text-gray-900">
                            {Number(r.clicks).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </AgentLayout>
  )
}
